import React, { useState, useEffect, useCallback } from 'react';
import { Card, Heading, Text, Flex, Avatar, Separator, Box, Button } from '@radix-ui/themes';
import { StarIcon, TargetIcon, CheckIcon, CalendarIcon, PersonIcon, EnvelopeClosedIcon, HomeIcon, IdCardIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { Member, FILE_SERVER_URL, apiService, MemberProfile, MemberProfileResponse } from './services/api';
import AppBar from './components/AppBar';
import NavigationDrawer from './components/NavigationDrawer';

interface ProfileProps {
    students: Member[];
    selectedStudent: Member | null;
    onStudentChange: (student: Member) => void;
}

const Profile: React.FC<ProfileProps> = ({ students, selectedStudent, onStudentChange }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [profileData, setProfileData] = useState<MemberProfile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Address edit modal state
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [addressForm, setAddressForm] = useState({
        address: '',
        city: '',
        state: '',
        pincode: ''
    });
    const [addressErrors, setAddressErrors] = useState<{ [key: string]: string }>({});

    // Parent information edit modal state
    const [isParentModalOpen, setIsParentModalOpen] = useState(false);
    const [parentForm, setParentForm] = useState({
        father_occupation: '',
        father_occupation_address: '',
        father_occupation_emailid: '',
        mother_occupation: '',
        mother_occupation_address: '',
        mother_occupation_emailid: ''
    });
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [parentErrors, setParentErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (!selectedStudent) return;

        setLoading(true);
        setError(null);

        apiService.getMemberProfile(selectedStudent.memberId)
            .then(response => {
                if (response.success) {
                    setProfileData(response.member);
                } else {
                    setError('Failed to fetch profile details');
                }
            })
            .catch(() => setError('Failed to fetch profile details'))
            .finally(() => setLoading(false));
    }, [selectedStudent]);

    // Calculate age from date of birth (using same logic as Dashboard)
    const calculateAge = (dateOfBirth: string) => {
        if (!dateOfBirth) return 'N/A';

        try {
            // Parse DD-MM-YYYY format
            const parts = dateOfBirth.split('-');
            if (parts.length !== 3) return 'N/A';

            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date constructor
            const year = parseInt(parts[2], 10);

            const dob = new Date(year, month, day);
            const today = new Date();

            let age = today.getFullYear() - dob.getFullYear();
            const monthDiff = today.getMonth() - dob.getMonth();

            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
                age--;
            }

            return age.toString();
        } catch (error) {
            return 'N/A';
        }
    };

    // Format date to proper format
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Format phone number
    const formatPhone = (phone: string) => {
        if (phone.length === 10) {
            return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        }
        return phone;
    };

    // Get membership status text and color
    const getMembershipStatus = (isActive: number) => {
        return isActive === 1
            ? { text: 'Active', color: 'var(--green-9)' }
            : { text: 'Inactive', color: 'var(--red-9)' };
    };

    // Handle address edit modal
    const handleAddressEdit = useCallback(() => {
        if (profileData) {
            setAddressForm({
                address: profileData.address || '',
                city: profileData.city || '',
                state: profileData.state || '',
                pincode: profileData.pincode || ''
            });
            setIsAddressModalOpen(true);
        }
    }, [profileData]);

    const handleAddressSave = async () => {
        if (!selectedStudent) return;

        // Validation
        const errors: { [key: string]: string } = {};

        if (!addressForm.address.trim()) {
            errors.address = 'Address is required';
        }
        if (!addressForm.city.trim()) {
            errors.city = 'City is required';
        }
        if (!addressForm.state.trim()) {
            errors.state = 'State is required';
        }
        if (!addressForm.pincode.trim()) {
            errors.pincode = 'Pincode is required';
        } else if (addressForm.pincode.length !== 6) {
            errors.pincode = 'Pincode must be 6 digits';
        }

        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        try {
            setLoading(true);
            setAddressErrors({});
            const response = await apiService.updateMailingDetail(selectedStudent.memberId, addressForm);

            if (response.success) {
                // Update local profile data with new address information
                if (profileData) {
                    setProfileData({
                        ...profileData,
                        address: addressForm.address,
                        city: addressForm.city,
                        state: addressForm.state,
                        pincode: addressForm.pincode
                    });
                }
                setIsAddressModalOpen(false);
                setSuccessMessage('Address information updated successfully!');
                setTimeout(() => {
                    setSuccessMessage(null);
                }, 3000);
            } else {
                setError('Failed to update address information');
            }
        } catch (error) {
            console.error('Error updating address:', error);
            setError('Failed to update address information');
        } finally {
            setLoading(false);
        }
    };

    const handleAddressCancel = useCallback(() => {
        setIsAddressModalOpen(false);
    }, []);

    // Handle parent information edit modal
    const handleParentEdit = () => {
        if (profileData) {
            setParentForm({
                father_occupation: profileData.father_occupation || '',
                father_occupation_address: profileData.father_occupation_address || '',
                father_occupation_emailid: profileData.father_occupation_emailid || '',
                mother_occupation: profileData.mother_occupation || '',
                mother_occupation_address: profileData.mother_occupation_address || '',
                mother_occupation_emailid: profileData.mother_occupation_emailid || ''
            });
            setIsParentModalOpen(true);
        }
    };

    const handleParentSave = async () => {
        if (!selectedStudent) return;

        // Validation
        const errors: { [key: string]: string } = {};

        // Father's details are required
        if (!parentForm.father_occupation.trim()) {
            errors.father_occupation = 'Father\'s occupation is required';
        }
        if (!parentForm.father_occupation_address.trim()) {
            errors.father_occupation_address = 'Father\'s address is required';
        }
        if (!parentForm.father_occupation_emailid.trim()) {
            errors.father_occupation_emailid = 'Father\'s email is required';
        } else if (!isValidEmail(parentForm.father_occupation_emailid)) {
            errors.father_occupation_emailid = 'Father\'s email is invalid';
        }

        // Mother's email validation (if provided)
        if (parentForm.mother_occupation_emailid.trim() && !isValidEmail(parentForm.mother_occupation_emailid)) {
            errors.mother_occupation_emailid = 'Mother\'s email is invalid';
        }

        setParentErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        try {
            setLoading(true);
            setParentErrors({});

            // Prepare father's data
            const fatherData = {
                father_occupation: parentForm.father_occupation.trim(),
                father_occupation_address: parentForm.father_occupation_address.trim(),
                father_occupation_emailid: parentForm.father_occupation_emailid.trim()
            };

            // Prepare mother's data (only include non-empty fields)
            const motherData: {
                mother_occupation?: string;
                mother_occupation_address?: string;
                mother_occupation_emailid?: string;
            } = {};

            if (parentForm.mother_occupation.trim()) {
                motherData.mother_occupation = parentForm.mother_occupation.trim();
            }
            if (parentForm.mother_occupation_address.trim()) {
                motherData.mother_occupation_address = parentForm.mother_occupation_address.trim();
            }
            if (parentForm.mother_occupation_emailid.trim()) {
                motherData.mother_occupation_emailid = parentForm.mother_occupation_emailid.trim();
            }

            // Call both APIs
            const [fatherResponse, motherResponse] = await Promise.all([
                apiService.updateFatherOccupationDetail(selectedStudent.memberId, fatherData),
                apiService.updateMotherOccupationDetail(selectedStudent.memberId, motherData)
            ]);

            // Check if both API calls were successful
            if (fatherResponse.success && motherResponse.success) {
                // Update local profile data
                if (profileData) {
                    setProfileData({
                        ...profileData,
                        father_occupation: fatherData.father_occupation,
                        father_occupation_address: fatherData.father_occupation_address,
                        father_occupation_emailid: fatherData.father_occupation_emailid,
                        mother_occupation: motherData.mother_occupation || null,
                        mother_occupation_address: motherData.mother_occupation_address || null,
                        mother_occupation_emailid: motherData.mother_occupation_emailid || null
                    });
                }
                setSuccessMessage('Parent information updated successfully!');
                setIsParentModalOpen(false);

                // Clear success message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage(null);
                }, 3000);
            } else {
                // Handle API errors
                const errorMessages = [];
                if (!fatherResponse.success) {
                    errorMessages.push('Failed to update father\'s information');
                }
                if (!motherResponse.success) {
                    errorMessages.push('Failed to update mother\'s information');
                }
                setError(errorMessages.join(', '));
            }
        } catch (error) {
            console.error('Error updating parent information:', error);
            setError('Failed to update parent information');
        } finally {
            setLoading(false);
        }
    };

    // Email validation function
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleParentCancel = useCallback(() => {
        setIsParentModalOpen(false);
    }, []);

    return (
        <div className="home-page">
            <AppBar
                onMenuClick={() => setIsDrawerOpen(true)}
                students={students}
                selectedStudent={selectedStudent}
                onStudentChange={onStudentChange}
            />
            <NavigationDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onLogout={() => window.location.href = '/'}
                selectedStudent={selectedStudent}
            />
            <main className="home-content">
                <div className="content-container">
                    {/* Welcome Section */}
                    <Card className="welcome-card">
                        <Flex align="center" gap="4">
                            <Avatar
                                size="4"
                                src={selectedStudent?.avatar && selectedStudent?.avatar !== 'NoImageFound.png' ? `${FILE_SERVER_URL}/show_files/${selectedStudent.avatar}` : undefined}
                                fallback={selectedStudent?.fullName?.charAt(0) || 'S'}
                            />
                            <Flex direction="column" gap="1">
                                <Heading size="6">Welcome, {selectedStudent?.fullName || 'Student'}!</Heading>
                                <Text size="2" color="gray">Here is your complete profile information.</Text>
                            </Flex>
                        </Flex>
                    </Card>

                    {/* Stats Section */}
                    <div className="profile-stats-grid">
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="blue">
                                    {profileData ? calculateAge(profileData.dateOfBirth) : 'N/A'}
                                </Text>
                                <Text size="2" color="gray">Age</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="green">
                                    {profileData?.bloodGroup || 'N/A'}
                                </Text>
                                <Text size="2" color="gray">Blood Group</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="orange">
                                    {profileData?.rank_title || 'N/A'}
                                </Text>
                                <Text size="2" color="gray">Current Rank</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color={profileData ? (getMembershipStatus(profileData.isActive).text === 'Active' ? 'green' : 'red') : 'gray'}>
                                    {profileData ? getMembershipStatus(profileData.isActive).text : 'N/A'}
                                </Text>
                                <Text size="2" color="gray">Status</Text>
                            </Flex>
                        </Card>
                    </div>

                    {/* Personal and Address Information Row */}
                    <div className="profile-info-grid">
                        {/* Personal Information */}
                        <Card className="activity-card">
                            <Heading size="4" mb="4">Personal Information</Heading>
                            {loading ? (
                                <Text size="3">Loading profile details...</Text>
                            ) : error ? (
                                <Text size="3" color="red">{error}</Text>
                            ) : profileData ? (
                                <Flex direction="column" gap="3">
                                    <Flex align="center" gap="3" className="activity-item">
                                        <div className="activity-icon">
                                            <PersonIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">Full Name</Text>
                                            <Text size="1" color="gray">{profileData.fullName}</Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--blue-9)', marginLeft: 'auto' }}>
                                            <IdCardIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                    <Flex align="center" gap="3" className="activity-item">
                                        <div className="activity-icon">
                                            <CalendarIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">Date of Birth</Text>
                                            <Text size="1" color="gray">{formatDate(profileData.dateOfBirth)}</Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--green-9)', marginLeft: 'auto' }}>
                                            <CalendarIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                    <Flex align="center" gap="3" className="activity-item">
                                        <div className="activity-icon">
                                            <IdCardIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">Contact Number</Text>
                                            <Text size="1" color="gray">{formatPhone(profileData.contactNumber)}</Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--orange-9)', marginLeft: 'auto' }}>
                                            <IdCardIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                    <Flex align="center" gap="3" className="activity-item">
                                        <div className="activity-icon">
                                            <EnvelopeClosedIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">Email Address</Text>
                                            <Text size="1" color="gray">{profileData.emailId || 'Not provided'}</Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--purple-9)', marginLeft: 'auto' }}>
                                            <EnvelopeClosedIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                </Flex>
                            ) : (
                                <Text size="3">No profile data available</Text>
                            )}
                        </Card>

                        {/* Address Information */}
                        <Card className="activity-card">
                            <Flex align="center" justify="between" mb="4">
                                <Heading size="4">Address Information</Heading>
                                <div
                                    style={{
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                    onClick={handleAddressEdit}
                                >
                                    <Pencil1Icon width="16" height="16" style={{ color: 'var(--gray-9)' }} />
                                </div>
                            </Flex>
                            {loading ? (
                                <Text size="3">Loading address details...</Text>
                            ) : error ? (
                                <Text size="3" color="red">{error}</Text>
                            ) : profileData ? (
                                <Flex direction="column" gap="3">
                                    <Flex align="center" gap="3" className="activity-item">
                                        <div className="activity-icon">
                                            <HomeIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">Address</Text>
                                            <Text size="1" color="gray">{profileData.address}</Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--blue-9)', marginLeft: 'auto' }}>
                                            <HomeIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                    <Flex align="center" gap="3" className="activity-item">
                                        <div className="activity-icon">
                                            <TargetIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">City & State</Text>
                                            <Text size="1" color="gray">{profileData.city}, {profileData.state}</Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--green-9)', marginLeft: 'auto' }}>
                                            <TargetIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                    <Flex align="center" gap="3" className="activity-item">
                                        <div className="activity-icon">
                                            <IdCardIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">Pincode</Text>
                                            <Text size="1" color="gray">{profileData.pincode}</Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: 'var(--orange-9)', marginLeft: 'auto' }}>
                                            <IdCardIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                </Flex>
                            ) : (
                                <Text size="3">No address data available</Text>
                            )}
                        </Card>
                    </div>

                    {/* Parent Information */}
                    <Card className="activity-card">
                        <Flex align="center" justify="between" mb="4">
                            <Heading size="4">Parent Information</Heading>
                            <div
                                style={{
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                onClick={handleParentEdit}
                            >
                                <Pencil1Icon width="16" height="16" style={{ color: 'var(--gray-9)' }} />
                            </div>
                        </Flex>
                        {loading ? (
                            <Text size="3">Loading parent details...</Text>
                        ) : error ? (
                            <Text size="3" color="red">{error}</Text>
                        ) : profileData ? (
                            <Flex direction="column" gap="3">
                                {/* Father's Information */}
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <PersonIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Father's Occupation</Text>
                                        <Text size="1" color="gray">{profileData.father_occupation}</Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--blue-9)', marginLeft: 'auto' }}>
                                        <PersonIcon width="16" height="16" />
                                    </div>
                                </Flex>
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <HomeIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Father's Address</Text>
                                        <Text size="1" color="gray">{profileData.father_occupation_address}</Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--green-9)', marginLeft: 'auto' }}>
                                        <HomeIcon width="16" height="16" />
                                    </div>
                                </Flex>
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <EnvelopeClosedIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Father's Email</Text>
                                        <Text size="1" color="gray">{profileData.father_occupation_emailid}</Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--orange-9)', marginLeft: 'auto' }}>
                                        <EnvelopeClosedIcon width="16" height="16" />
                                    </div>
                                </Flex>

                                {/* Mother's Information */}
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <PersonIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Mother's Occupation</Text>
                                        <Text size="1" color="gray">{profileData.mother_occupation || 'Not provided'}</Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--purple-9)', marginLeft: 'auto' }}>
                                        <PersonIcon width="16" height="16" />
                                    </div>
                                </Flex>
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <HomeIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Mother's Address</Text>
                                        <Text size="1" color="gray">{profileData.mother_occupation_address || 'Not provided'}</Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--purple-9)', marginLeft: 'auto' }}>
                                        <HomeIcon width="16" height="16" />
                                    </div>
                                </Flex>
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <EnvelopeClosedIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Mother's Email</Text>
                                        <Text size="1" color="gray">{profileData.mother_occupation_emailid || 'Not provided'}</Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--purple-9)', marginLeft: 'auto' }}>
                                        <EnvelopeClosedIcon width="16" height="16" />
                                    </div>
                                </Flex>
                            </Flex>
                        ) : (
                            <Text size="3">No parent data available</Text>
                        )}
                    </Card>
                </div>
            </main>

            {/* Success Message Toast */}
            {successMessage && (
                <div
                    className="toast-notification"
                    style={{
                        backgroundColor: 'var(--green-9)',
                        color: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1001,
                        animation: 'slideIn 0.3s ease-out',
                        wordWrap: 'break-word'
                    }}
                >
                    <Flex align="center" gap="2">
                        <CheckIcon width="16" height="16" />
                        <Text size="2" weight="bold">{successMessage}</Text>
                    </Flex>
                </div>
            )}

            {/* Address Edit Modal */}
            {isAddressModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '16px'
                }}>
                    <Card style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '24px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <Flex direction="column" gap="4">
                            <Flex align="center" justify="between">
                                <Heading size="5">Edit Address Information</Heading>
                                <div
                                    style={{
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                    onClick={handleAddressCancel}
                                >
                                    <CheckIcon width="16" height="16" style={{ color: 'var(--gray-9)' }} />
                                </div>
                            </Flex>

                            <Flex direction="column" gap="3">
                                <Flex direction="column" gap="2">
                                    <Text size="2" weight="bold">Address *</Text>
                                    <input
                                        type="text"
                                        value={addressForm.address}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressForm({ ...addressForm, address: e.target.value })}
                                        placeholder="Enter your address"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: addressErrors.address ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                    />
                                    {addressErrors.address && (
                                        <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                            {addressErrors.address}
                                        </Text>
                                    )}
                                </Flex>

                                <Flex gap="3">
                                    <Flex direction="column" gap="2" style={{ flex: 1 }}>
                                        <Text size="2" weight="bold">City *</Text>
                                        <input
                                            type="text"
                                            value={addressForm.city}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressForm({ ...addressForm, city: e.target.value })}
                                            placeholder="Enter city"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: addressErrors.city ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        {addressErrors.city && (
                                            <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                                {addressErrors.city}
                                            </Text>
                                        )}
                                    </Flex>
                                    <Flex direction="column" gap="2" style={{ flex: 1 }}>
                                        <Text size="2" weight="bold">State *</Text>
                                        <input
                                            type="text"
                                            value={addressForm.state}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressForm({ ...addressForm, state: e.target.value })}
                                            placeholder="Enter state"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: addressErrors.state ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        {addressErrors.state && (
                                            <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                                {addressErrors.state}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>

                                <Flex direction="column" gap="2">
                                    <Text size="2" weight="bold">Pincode (6 digits) *</Text>
                                    <input
                                        type="text"
                                        value={addressForm.pincode}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            setAddressForm({ ...addressForm, pincode: value });
                                        }}
                                        placeholder="Enter 6-digit pincode"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: addressErrors.pincode ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                    />
                                    {addressErrors.pincode && (
                                        <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                            {addressErrors.pincode}
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>

                            <Flex gap="3" justify="end" style={{ marginTop: '16px' }}>
                                <Button
                                    variant="soft"
                                    onClick={handleAddressCancel}
                                    style={{ minWidth: '80px' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="solid"
                                    onClick={handleAddressSave}
                                    disabled={loading}
                                    style={{
                                        minWidth: '100px',
                                        backgroundColor: 'var(--blue-9)',
                                        color: 'white'
                                    }}
                                >
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Flex>
                        </Flex>
                    </Card>
                </div>
            )}

            {/* Parent Information Edit Modal */}
            {isParentModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '16px'
                }}>
                    <Card style={{
                        width: '100%',
                        maxWidth: '600px',
                        maxHeight: '90vh',
                        padding: '24px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        overflow: 'auto'
                    }}>
                        <Flex direction="column" gap="4">
                            <Flex align="center" justify="between">
                                <Heading size="5">Edit Parent Information</Heading>
                                <div
                                    style={{
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                    onClick={handleParentCancel}
                                >
                                    <CheckIcon width="16" height="16" style={{ color: 'var(--gray-9)' }} />
                                </div>
                            </Flex>

                            <Flex direction="column" gap="4">
                                {/* Father's Information */}
                                <Flex direction="column" gap="3">
                                    <Heading size="4" color="blue">Father's Information *</Heading>

                                    <Flex direction="column" gap="2">
                                        <Text size="2" weight="bold">Occupation *</Text>
                                        <input
                                            type="text"
                                            value={parentForm.father_occupation}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParentForm({ ...parentForm, father_occupation: e.target.value })}
                                            placeholder="Enter father's occupation"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: parentErrors.father_occupation ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        {parentErrors.father_occupation && (
                                            <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                                {parentErrors.father_occupation}
                                            </Text>
                                        )}
                                    </Flex>

                                    <Flex direction="column" gap="2">
                                        <Text size="2" weight="bold">Address *</Text>
                                        <input
                                            type="text"
                                            value={parentForm.father_occupation_address}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParentForm({ ...parentForm, father_occupation_address: e.target.value })}
                                            placeholder="Enter father's address"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: parentErrors.father_occupation_address ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        {parentErrors.father_occupation_address && (
                                            <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                                {parentErrors.father_occupation_address}
                                            </Text>
                                        )}
                                    </Flex>

                                    <Flex direction="column" gap="2">
                                        <Text size="2" weight="bold">Email *</Text>
                                        <input
                                            type="email"
                                            value={parentForm.father_occupation_emailid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParentForm({ ...parentForm, father_occupation_emailid: e.target.value })}
                                            placeholder="Enter father's email"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: parentErrors.father_occupation_emailid ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        {parentErrors.father_occupation_emailid && (
                                            <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                                {parentErrors.father_occupation_emailid}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>

                                {/* Mother's Information */}
                                <Flex direction="column" gap="3">
                                    <Heading size="4" color="purple">Mother's Information (Optional)</Heading>

                                    <Flex direction="column" gap="2">
                                        <Text size="2" weight="bold">Occupation</Text>
                                        <input
                                            type="text"
                                            value={parentForm.mother_occupation}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParentForm({ ...parentForm, mother_occupation: e.target.value })}
                                            placeholder="Enter mother's occupation (optional)"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                    </Flex>

                                    <Flex direction="column" gap="2">
                                        <Text size="2" weight="bold">Address</Text>
                                        <input
                                            type="text"
                                            value={parentForm.mother_occupation_address}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParentForm({ ...parentForm, mother_occupation_address: e.target.value })}
                                            placeholder="Enter mother's address (optional)"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                    </Flex>

                                    <Flex direction="column" gap="2">
                                        <Text size="2" weight="bold">Email</Text>
                                        <input
                                            type="email"
                                            value={parentForm.mother_occupation_emailid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParentForm({ ...parentForm, mother_occupation_emailid: e.target.value })}
                                            placeholder="Enter mother's email (optional)"
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: parentErrors.mother_occupation_emailid ? '1px solid var(--red-9)' : '1px solid var(--gray-6)',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                        {parentErrors.mother_occupation_emailid && (
                                            <Text size="1" color="red" style={{ marginTop: '4px' }}>
                                                {parentErrors.mother_occupation_emailid}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                            </Flex>

                            <Flex gap="3" justify="end" style={{ marginTop: '16px' }}>
                                <Button
                                    variant="soft"
                                    onClick={handleParentCancel}
                                    style={{ minWidth: '80px' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="solid"
                                    onClick={handleParentSave}
                                    disabled={loading}
                                    style={{
                                        minWidth: '100px',
                                        backgroundColor: 'var(--blue-9)',
                                        color: 'white'
                                    }}
                                >
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Flex>
                        </Flex>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Profile; 